import { faBuilding } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { Plus } from "@vapor/react-icons";
import {
    Button,
    CircularProgress,
    ListItemIcon,
    ListItemText,
    Stack,
    useTheme,
} from "@vapor/react-material";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LinkableTaxPayerDto } from "../../../../../core/api/use-get-linkable-tax-payers/LinkableTaxPayerDto";
import { useLinkTaxPayerToFulfilment } from "../../../../../core/api/use-link-tax-payer-to-fulfilment/useLinkTaxPayerToFulfilment";
import { useUnlinkTaxPayerFromFulfilment } from "../../../../../core/api/use-unlink-tax-payer-from-fulfilment/useUnlinkTaxPayerFromFulfilment";
import { isFiscalCode } from "../../../../../utils/taxPayer";

enum AssociationStatus {
    NOT_REQUESTED,
    REQUESTED_SUCCESS,
    REQUESTED_ERROR,
}

export interface FulfilmentAssociationTaxPayerProps {
    levyId: string;
    taxPayer: LinkableTaxPayerDto;
    year: number;
}

export const FulfilmentAssociationTaxPayer = ({
    levyId,
    taxPayer,
    year,
}: FulfilmentAssociationTaxPayerProps): ReactElement => {
    const theme = useTheme();
    const { t } = useTranslation();

    // states
    const [associationStatus, setAssociationStatus] = useState(
        AssociationStatus.NOT_REQUESTED,
    );

    const [dissociationStatus, setDissociationStatus] = useState(
        AssociationStatus.NOT_REQUESTED,
    );

    // use cases
    const linkTaxPayerMutation = useLinkTaxPayerToFulfilment();
    const unlinkTaxPayerMutation = useUnlinkTaxPayerFromFulfilment();

    // functions
    const onClickLinkTaxPayerToFulfilment = async () => {
        try {
            await linkTaxPayerMutation.mutateAsync({
                taxPayerWorkspaceId: taxPayer.workspaceId,
                levyId: levyId,
                year: year,
            });
            setAssociationStatus(AssociationStatus.REQUESTED_SUCCESS);
            setDissociationStatus(AssociationStatus.NOT_REQUESTED);
        } catch {
            setAssociationStatus(AssociationStatus.REQUESTED_ERROR);
        }
    };

    const onClickUnlinkTaxPayerToFulfilment = async () => {
        try {
            await unlinkTaxPayerMutation.mutateAsync({
                taxPayerWorkspaceId: taxPayer.workspaceId,
                levyId: levyId,
                year: year,
            });
            setDissociationStatus(AssociationStatus.REQUESTED_SUCCESS);
            setAssociationStatus(AssociationStatus.NOT_REQUESTED);
        } catch {
            setDissociationStatus(AssociationStatus.REQUESTED_ERROR);
        }
    };

    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            width="100%"
            sx={{
                px: 2,
                py: 1,
            }}
        >
            {/* left */}
            <Stack direction="row" alignItems="center">
                <ListItemIcon
                    sx={{
                        alignItems: "center",
                        borderRadius: "50%",
                        backgroundColor: theme.palette.primary.background,
                        display: "flex",
                        justifyContent: "center",
                        marginRight: 1,
                        height: 40,
                        width: 40,
                    }}
                >
                    <FontAwesomeIcon
                        color={theme.palette.primary.interactiveDefault}
                        icon={faBuilding}
                    />
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Typography
                            sx={{
                                color: theme.palette.text.primary,
                            }}
                            variant="bodyLarge"
                        >
                            {taxPayer.name}
                        </Typography>
                    }
                    secondary={
                        <Typography
                            sx={{
                                color: theme.palette.primary.textSubduedColor,
                            }}
                            variant="body"
                        >
                            {isFiscalCode(taxPayer.fiscalCode) ? (
                                <>
                                    {t(
                                        "views.home.components.taxPayer.tax_identifier.taxId",
                                        { taxId: taxPayer.fiscalCode },
                                    )}
                                </>
                            ) : (
                                <>
                                    {t(
                                        "views.home.components.taxPayer.tax_identifier.vatId",
                                        {
                                            vatId:
                                                taxPayer.fiscalCode ||
                                                taxPayer.vatNumber,
                                        },
                                    )}
                                </>
                            )}
                        </Typography>
                    }
                />
            </Stack>
            {/* right */}
            <Stack alignSelf="center">
                {associationStatus !== AssociationStatus.REQUESTED_SUCCESS && (
                    <Button
                        disabled={linkTaxPayerMutation.isPending}
                        onClick={onClickLinkTaxPayerToFulfilment}
                        startIcon={<Plus />}
                        variant="outlined"
                    >
                        <Typography>
                            {associationStatus ===
                                AssociationStatus.NOT_REQUESTED &&
                                t(
                                    "views.home.components.fulfilment_association_taxPayer.actions.link",
                                )}

                            {associationStatus ===
                                AssociationStatus.REQUESTED_ERROR &&
                                t(
                                    "views.home.components.fulfilment_association_taxPayer.actions.retry_link",
                                )}
                        </Typography>
                        {linkTaxPayerMutation.isPending && (
                            <CircularProgress
                                color="inherit"
                                sx={{ ml: 1 }}
                                size={16}
                            />
                        )}
                    </Button>
                )}
                {associationStatus === AssociationStatus.REQUESTED_SUCCESS &&
                    (dissociationStatus === AssociationStatus.NOT_REQUESTED ||
                        dissociationStatus ===
                            AssociationStatus.REQUESTED_ERROR) && (
                        <Button
                            disabled={unlinkTaxPayerMutation.isPending}
                            onClick={onClickUnlinkTaxPayerToFulfilment}
                        >
                            <Typography>
                                {t(
                                    "views.home.components.fulfilment_association_taxPayer.actions.unlink",
                                )}
                            </Typography>
                            {unlinkTaxPayerMutation.isPending && (
                                <CircularProgress
                                    color="inherit"
                                    sx={{ ml: 1 }}
                                    size={16}
                                />
                            )}
                        </Button>
                    )}
            </Stack>
        </Stack>
    );
};
